/**
 * `gamification-core` — CONFIG del dominio field-check.
 *
 * Retrofit OPT-IN y NO punitivo (B2B): la gamificación premia el trabajo de
 * campo bien hecho (inspecciones completadas, hallazgos resueltos, constancia)
 * como ENGAGEMENT, nunca como vigilancia del trabajador. Ligas privadas por
 * defecto (§leagues). El motor es agnóstico; aquí solo declaramos las reglas.
 *
 * La gamificación DERIVA del `activity` (hash-chain inmutable) — ver `derive.ts`.
 * No altera el log: es una proyección pura y recomputable.
 */
import { counterAtLeast, streakAtLeast } from "./badges";
import type { GamificationConfig } from "./engine";

/** Tipos de evento de dominio (derivados del feed de `activity`). */
export const FIELDCHECK_EVENTS = {
  inspectionCreated: "inspection.created",
  inspectionCompleted: "inspection.completed",
  findingResolved: "finding.resolved",
} as const;

/** Línea de racha: días con al menos una inspección completada (constancia). */
export const FIELDWORK_STREAK_LINE = "fieldwork";

export const fieldcheckGamificationConfig: GamificationConfig = {
  xp: {
    [FIELDCHECK_EVENTS.inspectionCompleted]: 25,
    [FIELDCHECK_EVENTS.findingResolved]: 15,
    [FIELDCHECK_EVENTS.inspectionCreated]: 5,
  },
  streakLines: {
    [FIELDCHECK_EVENTS.inspectionCompleted]: [FIELDWORK_STREAK_LINE],
  },
  counters: {
    [FIELDCHECK_EVENTS.inspectionCompleted]: ["inspections"],
    [FIELDCHECK_EVENTS.findingResolved]: ["findings_resolved"],
  },
  badges: [
    {
      id: "first-inspection",
      title: "First inspection",
      description: "Complete your first inspection.",
      when: counterAtLeast("inspections", 1),
    },
    {
      id: "ten-inspections",
      title: "Ten inspections",
      description: "Complete ten inspections.",
      when: counterAtLeast("inspections", 10),
    },
    {
      id: "steady-week",
      title: "Steady week",
      description: "Complete inspections seven days in a row.",
      when: streakAtLeast(FIELDWORK_STREAK_LINE, 7),
      bonusXp: 50,
    },
    {
      id: "closer",
      title: "Closer",
      description: "Resolve ten findings.",
      when: counterAtLeast("findings_resolved", 10),
    },
  ],
  quests: [
    {
      id: "daily-inspections",
      title: "Three inspections today",
      period: "daily",
      goals: [{ metric: FIELDCHECK_EVENTS.inspectionCompleted, target: 3 }],
      bonusXp: 20,
    },
  ],
};
